import { FC } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import Discover from '../../../common/redux/actions/DiscoverActions';
import Dataset from '../../../common/redux/actions/DatasetActions';
import {
  useHiddenFieldWarnings,
  useOpenVizDatasetSelector,
} from '../../../common/redux/selectors/viz-selector.hook';
import Util from '../../../common/Util';
import { isDashletMode as getIsDashletMode } from '../../../auth';
import { messages } from '../../../i18n';
import { PrimaryButton } from '../../../ui';
import { ChartPlaceHolder } from '../chart-placeholder.component';
import { IHiddenFieldWarning } from '../../VizUtil';
import { IHiddenFieldPlaceholderProps } from './hidden-field-placeholder.interfaces';

const describeCauses = (
  { name, causes }: IHiddenFieldWarning,
  isHidden: (name: string) => boolean,
): string => {
  if (causes.length === 1 && causes[0] === name) {
    return isHidden(name)
      ? messages.baseChart.hiddenReasonHidden
      : messages.baseChart.hiddenReasonUnavailable;
  }
  const template = _.every(causes, isHidden)
    ? messages.baseChart.hiddenReasonUses
    : messages.baseChart.hiddenReasonUsesUnavailable;
  return messages.formatString(template, causes.join(', ')) as string;
};

export const HiddenFieldPlaceholder: FC<IHiddenFieldPlaceholderProps> = ({
  vizId,
}) => {
  const dispatch = useDispatch();
  const dataset = useOpenVizDatasetSelector({ discoveryId: vizId });
  const { hiddenFields, hiddenFilters, hiddenSettings, fieldsToMakeVisible } =
    useHiddenFieldWarnings({ discoveryId: vizId });

  if (getIsDashletMode()) {
    return (
      <div className={'viz-placeholder'}>
        <span>{messages.baseChart.reportHiddenFields}</span>
        <span>{messages.baseChart.openDiscoverToFixHidden}</span>
      </div>
    );
  }

  const isHidden = (name: string) => fieldsToMakeVisible.includes(name);
  const lines = [
    ...hiddenFields.map(warning => ({
      key: `field:${warning.name}`,
      label: warning.name,
      warning,
    })),
    ...hiddenFilters.map(warning => ({
      key: `filter:${warning.name}`,
      label: messages.formatString(
        messages.baseChart.hiddenFilterLabel,
        warning.name,
      ),
      warning,
    })),
    ...hiddenSettings.map(warning => ({
      key: `setting:${warning.name}`,
      label: messages.formatString(
        messages.baseChart.hiddenSettingLabel,
        warning.name,
      ),
      warning,
    })),
  ];

  const canRemove = !_.isEmpty(hiddenFields) || !_.isEmpty(hiddenFilters);
  const hasFieldsToMakeVisible = !_.isEmpty(fieldsToMakeVisible);
  const canMakeVisible =
    hasFieldsToMakeVisible && Util.isDatasetEditable(dataset);

  const removeFields = () => {
    dispatch(
      Discover.removeHiddenFields(
        vizId,
        _.map(hiddenFields, 'name'),
        _.map(hiddenFilters, 'name'),
      ),
    );
    dispatch(Discover.setDiscoveryDirty(vizId, true));
  };

  const makeFieldsVisible = () => {
    dispatch(Discover.setConfigPanelDetail(vizId, 'layout'));
    dispatch(Dataset.editDataset(dataset.id, true));
  };

  return (
    <ChartPlaceHolder vizId={vizId} hasError>
      <p>{messages.baseChart.hiddenFieldsHeader}</p>
      <ul>
        {lines.map(({ key, label, warning }) => (
          <li key={key}>
            {messages.formatString(
              messages.baseChart.hiddenLine,
              label,
              describeCauses(warning, isHidden),
            )}
          </li>
        ))}
      </ul>
      {hasFieldsToMakeVisible && !canMakeVisible && (
        <p>
          {messages.formatString(
            messages.baseChart.askAdminToMakeVisible,
            fieldsToMakeVisible.join(', '),
          )}
        </p>
      )}
      {canRemove && (
        <PrimaryButton onClick={removeFields}>
          {messages.baseChart.removeHiddenFields}
        </PrimaryButton>
      )}
      {canMakeVisible && (
        <PrimaryButton onClick={makeFieldsVisible}>
          {messages.baseChart.makeFieldsVisible}
        </PrimaryButton>
      )}
    </ChartPlaceHolder>
  );
};
